import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import photoExtSoleil from '../assets/images/voyage-van-amenage-coucher-soleil-road-trip.jpg';
import photoLandscapeMSM from '../assets/images/voyage-van-matin-lever-soleil-montagne.jpg';
import photoMontSaintMichel from '../assets/images/road-trip-van-mont-saint-michel-normandie.jpg';
import photoRouteMontagne from '../assets/images/voyage-van-amenage-route-montagne-france.jpg';
import photoVueExtInt from '../assets/images/interieur-van-california-vue-paysage-coffre.jpg';
import photoVueExt from '../assets/images/interieur-van-california-vue-exterieure-paysage.jpg';

const About: React.FC = () => {
  return (
    <div className="bg-white font-source">
      <Helmet>
        <title>À propos — L'histoire de To The Road</title>
        <meta name="description" content="To The Road, c'est une passion née sur les routes. Découvrez notre histoire, notre flotte de vans aménagés et notre vision de la liberté sur la route." />
        <link rel="canonical" href="https://totheroad.fr/a-propos" />
        <meta property="og:title" content="À propos — L'histoire de To The Road" />
        <meta property="og:description" content="To The Road, c'est une passion née sur les routes. Découvrez notre histoire, notre flotte de vans aménagés et notre vision de la liberté sur la route." />
        <meta property="og:image" content="https://totheroad.fr/og-image.jpg" />
        <meta property="og:url" content="https://totheroad.fr/a-propos" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* SECTION HISTOIRE */}
      <section className="py-24 md:py-32 px-4 bg-gradient-to-b from-blue-50 via-white to-blue-50">
        <div className="max-w-5xl mx-auto">
          {/* Titre principal */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 font-quicksand">
              À propos de nous
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto rounded-full"></div>
          </div>
          
          {/* Contenu de l'histoire */}
          <div className="bg-white rounded-3xl shadow-2xl p-10 md:p-16 lg:p-20">
            <div className="space-y-5 text-base md:text-lg text-gray-700 leading-relaxed">
              <p className="first-letter:text-5xl first-letter:font-bold first-letter:text-blue-600 first-letter:mr-2 first-letter:float-left">
                Tout a commencé il y a dix ans, sur les routes. Des week-ends improvisés, des étés entiers à explorer, des levers de soleil face à l'océan. Des rencontres inoubliables avec d'autres voyageurs qui partageaient la même soif de liberté.
              </p>
              <p>
                Au fil des années, cette passion s'est enrichie d'une conviction : voyager autrement, c'est aussi respecter ce qui nous entoure. Vivre dans un van, c'est apprendre à consommer différemment. Moins d'espace, moins d'énergie, moins de superflu. Juste l'essentiel pour profiter pleinement de la nature.
              </p>
              <p>
                C'est pour cela que la vanlife est, en elle-même, un choix engagé. Et pour ceux qui veulent aller encore plus loin, certains de nos véhicules sont électriques — parce que le silence d'un moteur rend chaque instant plus intense, et que réduire notre impact, c'est préserver les paysages qu'on aime tant découvrir.
              </p>
              <p>
                Chaque départ est une promesse d'aventure. Chaque arrêt, une découverte. Chaque nuit dans le van, un moment de simplicité et de connexion avec l'essentiel.
              </p>
              
              {/* Citation finale mise en valeur */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-xl md:text-2xl font-semibold text-blue-600 italic text-center leading-relaxed">
                  Alors nous avons voulu partager cette expérience avec vous.
                </p>
                <p className="text-xl md:text-2xl font-semibold text-blue-600 italic text-center leading-relaxed">
                  Une aventure qui vous ressemble, dans le respect de ce qui nous entoure.
                </p>
                <p className="text-xl md:text-2xl font-semibold text-blue-600 italic text-center leading-relaxed">
                  C'est comme ça qu'est né To The Road.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION DIFFÉRENCES */}
      <section className="py-20 px-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Card 1 */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow duration-300">
              <div className="text-5xl mb-4">🚐</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-quicksand">Modulables</h3>
              <p className="text-gray-600 leading-relaxed">
                Aménagés ou pas. Vos besoins changent ? Nos véhicules aussi.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow duration-300">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-quicksand">Sur mesure</h3>
              <p className="text-gray-600 leading-relaxed">
                Un échange par message. Un formulaire pour vous accompagner dans votre choix. Un appel en fonction de vos besoins.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow duration-300">
              <div className="text-5xl mb-4">🌿</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-quicksand">Atypiques</h3>
              <p className="text-gray-600 leading-relaxed">
                Des véhicules qu'on ne voit pas partout. Parce que votre voyage ne ressemble à aucun autre.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION ENGAGEMENTS */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16 font-quicksand">
            Ce qu'on vous promet
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            
            {/* Engagement 1 */}
            <div className="text-center">
              <div className="text-5xl mb-4">✈️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-quicksand">L'esprit liberté</h3>
              <p className="text-gray-600 leading-relaxed">
                Pas de contraintes. Juste la route devant vous.
              </p>
            </div>

            {/* Engagement 2 */}
            <div className="text-center">
              <div className="text-5xl mb-4">🔧</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-quicksand">Des véhicules soignés</h3>
              <p className="text-gray-600 leading-relaxed">
                Chaque détail compte. Comme si c'était le nôtre.
              </p>
            </div>

            {/* Engagement 3 */}
            <div className="text-center">
              <div className="text-5xl mb-4">🎨</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-quicksand">La flexibilité</h3>
              <p className="text-gray-600 leading-relaxed">
                Votre projet évolue ? On s'adapte.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION CHIFFRES - VERSION SIMPLIFIÉE */}
      <section className="py-20 px-4 bg-gradient-to-b from-blue-50 to-blue-100">
        <div className="max-w-5xl mx-auto text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            
            <div>
              <div className="text-5xl md:text-6xl font-bold text-blue-600 mb-3">1</div>
              <p className="text-xl text-gray-700 font-medium">Obsession</p>
              <p className="text-gray-600 mt-2">Votre satisfaction</p>
            </div>

            <div>
              <div className="text-5xl md:text-6xl font-bold text-blue-600 mb-3">100%</div>
              <p className="text-xl text-gray-700 font-medium">Passion</p>
              <p className="text-gray-600 mt-2">&amp; disponibilité</p>
            </div>

            <div>
              <div className="text-5xl md:text-6xl font-bold text-blue-600 mb-3">∞</div>
              <p className="text-xl text-gray-700 font-medium">Possibilités</p>
              <p className="text-gray-600 mt-2">d'aventures</p>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION GALERIE */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16 font-quicksand">
            Ils ont pris la route
          </h2>

          {/* Grille photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            
            {/* Photo 1 */}
            <div className="rounded-xl overflow-hidden shadow-lg h-72 hover:scale-105 transition-transform duration-300">
              <img src={photoExtSoleil} alt="Van aménagé au coucher du soleil lors d'un road trip" className="w-full h-full object-cover" />
            </div>

            {/* Photo 2 */}
            <div className="rounded-xl overflow-hidden shadow-lg h-72 hover:scale-105 transition-transform duration-300">
              <img src={photoLandscapeMSM} alt="Paysage du Mont Saint-Michel vu depuis un van en road trip" className="w-full h-full object-cover" />
            </div>

            {/* Photo 3 */}
            <div className="rounded-xl overflow-hidden shadow-lg h-72 hover:scale-105 transition-transform duration-300">
              <img src={photoMontSaintMichel} alt="Road trip en van au Mont Saint-Michel en Normandie" className="w-full h-full object-cover" />
            </div>

            {/* Photo 4 */}
            <div className="rounded-xl overflow-hidden shadow-lg h-72 hover:scale-105 transition-transform duration-300">
              <img src={photoRouteMontagne} alt="Van aménagé sur une route de montagne en France" className="w-full h-full object-cover" />
            </div>

            {/* Photo 5 */}
            <div className="rounded-xl overflow-hidden shadow-lg h-72 hover:scale-105 transition-transform duration-300">
              <img src={photoVueExtInt} alt="Intérieur van California avec vue sur le paysage depuis le coffre" className="w-full h-full object-cover" />
            </div>

            {/* Photo 6 */}
            <div className="rounded-xl overflow-hidden shadow-lg h-72 hover:scale-105 transition-transform duration-300">
              <img src={photoVueExt} alt="Vue extérieure depuis l'intérieur d'un van California en location" className="w-full h-full object-cover" />
            </div>

          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-2xl md:text-3xl font-semibold text-gray-900 mb-8">
              Prêt pour votre aventure ?
            </p>
            <Link 
              to="/reserver" 
              className="inline-block bg-blue-500 hover:bg-blue-600 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 shadow-md hover:shadow-xl"
            >
              Découvrir nos véhicules
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;